import { keepPreviousData, useQuery } from '@tanstack/react-query'
import EcommerceApi from '@/infrastructure/api/ecommerce'
import { CatalogKind } from '@/domain/catalog'

export function useCatalogSearch(
  ecommerceId: string | undefined,
  kind: CatalogKind,
  query: string,
  enabled: boolean
) {
  const searchQuery = useQuery({
    queryKey: ['catalogSearch', ecommerceId, kind, query],
    queryFn: ({ signal }) =>
      EcommerceApi.searchCatalog(
        { ecommerceId: ecommerceId!, kind, query },
        signal
      ),
    enabled: enabled && !!ecommerceId,
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
    // A store that refuses the search (e.g. an app without the scope) keeps refusing it.
    retry: 1
  })
  return {
    results: searchQuery.data ?? [],
    searching: searchQuery.isFetching,
    loading: searchQuery.isLoading,
    loaded: searchQuery.isSuccess,
    failed: searchQuery.isError
  }
}

// Labels for ids a rule already stores; an id missing from the result no longer exists in the store.
export function useCatalogItems(
  ecommerceId: string | undefined,
  kind: CatalogKind,
  ids: string[]
) {
  const itemsQuery = useQuery({
    queryKey: ['catalogItems', ecommerceId, kind, ids],
    queryFn: ({ signal }) =>
      EcommerceApi.searchCatalog(
        { ecommerceId: ecommerceId!, kind, ids },
        signal
      ),
    enabled: !!ecommerceId && ids.length > 0,
    staleTime: 1000 * 60 * 5,
    retry: 1
  })
  return {
    items: itemsQuery.data ?? [],
    loading: itemsQuery.isLoading,
    resolved: ids.length === 0 || itemsQuery.isSuccess
  }
}
